import {
  Text,
  GreyGrey70,
  Primary13Regular,
  Primary13SemiBold,
  Box,
  Flex
} from '@sequencehq/core-components'
import {
  PriceField,
  InputSelectControlField,
  TextInputField,
  SteppedNumberField,
  CheckboxLineField,
  CheckboxLineInput
} from '@sequencehq/core-components'
import { useState } from 'react'
import { Currency } from '@sequencehq/api'
import { useSeatBasedPriceForm } from './useSeatBasedPriceForm'
import { PricingModelField } from '../common/PricingModelField'
import { SeatOveragesBillingFrequency } from 'common/drawers/PricingEditor/domain'
import { SeatBasedTierInput } from './SeatBasedTierInput/SeatBasedTierInput'
import { ExternalLedgerForm } from '../common/ExternalLedgerForm/ExternalLedgerForm'
import SeatProrationDropdown from 'common/drawers/PricingEditor/view/editors/SeatBasedPrice/SeatProrationDropdown/SeatProrationDropdown.tsx'
import { BillingTypeField } from '../common/BillingTypeField'
import { BillingFrequencyField } from '../common/BillingFrequencyField'
import { ProductSelectField } from '../common/ProductSelectField'
import { CopyPricingTableButton } from '../common/CopyPricingTableButton'
import { SeatMetricSelect } from 'Seats/SeatMetric/SeatMetricSelect'

export const SeatBasedPriceForm = () => {
  const { fieldsConfig, currency, conditions } = useSeatBasedPriceForm()
  const [showMinimumSeats, setShowMinimumSeats] = useState(
    conditions.isContractedMinimumSet
  )

  const toolTipBoxes = fieldsConfig.overagesBillingFrequency.options.map(
    value => (
      <>
        <Box {...Primary13SemiBold} key={value.value} marginBottom="8px">
          {value.label}
        </Box>
        <Box marginBottom="16px" key={value.description}>
          {value.description}
        </Box>
      </>
    )
  )

  return (
    <>
      <Flex direction="column" gap="16px">
        <ProductSelectField />

        <PricingModelField />

        <TextInputField
          label="Display name (appears on invoice)"
          data-testid="seat.name"
          value={fieldsConfig.name.value}
          onChange={e => fieldsConfig.name.onChange(e.target.value)}
          validationErrors={fieldsConfig.name.validationErrors}
          isDisabled={fieldsConfig.name.disabled}
          placeholder="Enter a display name"
        />
        <BillingFrequencyField
          data-testid="seat.billingFrequency"
          value={fieldsConfig.billingFrequency.value}
          options={fieldsConfig.billingFrequency.options}
          onChange={fieldsConfig.billingFrequency.onChange}
          validationErrors={fieldsConfig.billingFrequency.validationErrors}
          isDisabled={fieldsConfig.billingFrequency.disabled}
        />
        <BillingTypeField
          value={fieldsConfig.billingType.value}
          onChange={newValue => {
            fieldsConfig.billingType.onChange(newValue)
          }}
          isDisabled={fieldsConfig.billingType.disabled}
          data-testid="seat.billingType"
        />
        <SeatMetricSelect
          label="Seat metric"
          data-testid="seat.seatMetric"
          value={fieldsConfig.seatMetricId.value}
          onChange={fieldsConfig.seatMetricId.onChange}
          validationErrors={fieldsConfig.seatMetricId.validationErrors}
          disabled={fieldsConfig.seatMetricId.disabled}
        />
        <CheckboxLineField
          isDisabled={fieldsConfig.contractedMinimumSeats.disabled}
          value={showMinimumSeats}
          onChange={() => {
            const newValue = !showMinimumSeats

            if (!newValue) {
              fieldsConfig.contractedMinimumSeats.onChange('0')
            }

            setShowMinimumSeats(newValue)
          }}
          data-testid="seat.showMinimumSeats"
          text="Contract a minimum number of seats"
        />
        {showMinimumSeats && (
          <Box display="flex" gap="8px">
            <SteppedNumberField
              data-testid="seat.contractedMinimumSeats"
              label=""
              value={fieldsConfig.contractedMinimumSeats.value}
              precision={0}
              onChange={newValue =>
                fieldsConfig.contractedMinimumSeats.onChange(newValue)
              }
              validationErrors={
                fieldsConfig.contractedMinimumSeats.validationErrors
              }
              isDisabled={fieldsConfig.contractedMinimumSeats.disabled}
              width="128px"
            />
            <Text {...Primary13Regular} color={GreyGrey70} pt={2}>
              Included
            </Text>
          </Box>
        )}

        {!fieldsConfig.pricePerSeat.hidden ? (
          <Flex gap="4px" alignItems="flex-start">
            <PriceField
              label="Seat price"
              data-testid="seat.pricePerSeat"
              value={fieldsConfig.pricePerSeat.value}
              onChange={newValue =>
                fieldsConfig.pricePerSeat.onChange(newValue)
              }
              validationErrors={fieldsConfig.pricePerSeat.validationErrors}
              currency={currency}
              isDisabled={fieldsConfig.pricePerSeat.disabled}
              borderTopRightRadius={0}
              borderBottomRightRadius={0}
              variant="v2"
            />
            {!fieldsConfig.currency.hidden && (
              <Box paddingTop="24px">
                <InputSelectControlField
                  data-testid="seat.currency"
                  value={fieldsConfig.currency.value}
                  onChange={newCurrency =>
                    fieldsConfig.currency.onChange(newCurrency as Currency)
                  }
                  options={fieldsConfig.currency.options}
                  validationErrors={fieldsConfig.currency.validationErrors}
                  disabled={fieldsConfig.currency.disabled}
                  placeholder="Select currency"
                  width="150px"
                />
              </Box>
            )}
          </Flex>
        ) : (
          <Box width="100%">
            <Flex gap={4} alignItems="center" marginBottom="8px">
              <Box {...Primary13SemiBold} color={GreyGrey70}>
                Tiers
              </Box>
              {!fieldsConfig.currency.hidden && (
                <InputSelectControlField
                  data-testid="seat.currency"
                  value={fieldsConfig.currency.value}
                  onChange={newCurrency =>
                    fieldsConfig.currency.onChange(newCurrency as Currency)
                  }
                  options={fieldsConfig.currency.options}
                  validationErrors={fieldsConfig.currency.validationErrors}
                  disabled={fieldsConfig.currency.disabled}
                  placeholder="Select currency"
                  width="90px"
                />
              )}
              <Flex grow="1"></Flex>
              <CopyPricingTableButton
                data={{ tiers: fieldsConfig.tiers.value, currency }}
                data-testid="seat.copyPricingTable"
              />
            </Flex>
            <SeatBasedTierInput
              value={fieldsConfig.tiers.value}
              onChange={fieldsConfig.tiers.onChange}
              isDisabled={fieldsConfig.tiers.disabled}
              currency={currency}
              validationErrors={fieldsConfig.tiers.validationErrors}
            />
          </Box>
        )}

        {fieldsConfig.overagesBillingFrequency.value &&
          conditions.isInAdvanceSelected &&
          conditions.isBillingFrequencyLessThanMonthly && (
            <InputSelectControlField
              label="Overages billing frequency"
              data-testid="seat.overagesBillingFrequency"
              tooltip={{
                title: 'Overages billing frequency',
                content: <Box paddingTop="8px">{toolTipBoxes}</Box>
              }}
              value={fieldsConfig.overagesBillingFrequency.value}
              onChange={newValue =>
                fieldsConfig.overagesBillingFrequency.onChange(
                  newValue as SeatOveragesBillingFrequency
                )
              }
              options={fieldsConfig.overagesBillingFrequency.options}
              validationErrors={
                fieldsConfig.overagesBillingFrequency.validationErrors
              }
              disabled={fieldsConfig.overagesBillingFrequency.disabled}
              placeholder="Select overage billing frequency"
            />
          )}
        <SeatProrationDropdown
          data-testid="seat.prorationStrategy"
          value={fieldsConfig.prorationStrategy.value}
          disabled={fieldsConfig.prorationStrategy.disabled}
          onChange={fieldsConfig.prorationStrategy.onChange}
        />
        {!fieldsConfig.prorateFlatFees.hidden && (
          <CheckboxLineInput
            text="Prorate flat fees"
            description="Charge a prorated flat fee for partial billing periods."
            value={fieldsConfig.prorateFlatFees.value}
            onChange={() => {
              fieldsConfig.prorateFlatFees.onChange(
                !fieldsConfig.prorateFlatFees.value
              )
            }}
            data-testid="seat.prorateFlatFees"
            isDisabled={fieldsConfig.prorateFlatFees.disabled}
          />
        )}

        <ExternalLedgerForm />
      </Flex>
    </>
  )
}
